// Sjekk servicekapasitet (/sjekk-kapasitet): når kan kunden levere, og når er sykkelen eller
// skiene ferdige? I samme stil som startsiden (planer/ny-kundesamhandling-plan.md): først hva
// det gjelder og hva som skal gjøres, som store knapper, så svaret per butikk. Ski har et steg
// til, Alpint eller Langrenn, så hver liste med tjenester blir kort.
//
// Sykkel: samme beregning som «Bookingkapasitet i sanntid» på kundenettsiden
// (components/workshop/capacityCheckLogic.js). Ski og skøyter: samme tjenester (tjenestekatalogen) og
// beregning som «Foreslå ferdigdato» på servicesiden (components/workshop/skiCapacityLogic.js).
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getButikkKapasiteter, getKapasitetsOverstyringer, getServiceBySlug, getSkiSkoyteTjenester, getSykkelBookingtyper } from "./lib/tjenestekatalogApi";
import { normalizeWorkshopSlug, prettifyStoreName, isoDate, fmtMinutes } from "./components/workshop/workshopCapacityUtils";
import { FILTERS, computeStoreResults, ferdigVindu, normBookingtype } from "./components/workshop/capacityCheckLogic";
import { computeFerdigDatoForskslag } from "./components/workshop/skiCapacityLogic";
import { FARGER, FORSIDE } from "./ny-kundesamhandling/handlinger";
import Ikon from "./ny-kundesamhandling/Ikon";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_MINUTTER = { generell: 60, girbremse: 20, reklamasjon: 10, dekk: 10 };
const DAGER_FRAMOVER = 140;

const KATEGORIER = [
  { id: "sykkel", tittel: "Sykkel", ikon: "sykkel", farge: "verksted" },
  { id: "ski", tittel: "Ski", ikon: "snofnugg", farge: "lager" },
  { id: "skoyter", tittel: "Skøyter", ikon: "skoyte", farge: "lager" },
];
const GRENER = [
  { id: "Alpint", tittel: "Alpint", ikon: "fjell" },
  { id: "Langrenn", tittel: "Langrenn", ikon: "skispor" },
];

const OPPGAVE_IKON = { generell: "skiftenokkel", girbremse: "gjenta", reklamasjon: "skjold", dekk: "dekk" };

// Bookingtypene slik kundene møter dem på nettsiden, med hva de betyr for levering og henting.
const TYPER = {
  dag: { navn: "Dagservice", forklaring: "Leveres dagen før eller tidlig på servicedagen, ferdig samme dag" },
  tidliguke: { navn: "Tidlig i uka", forklaring: "Leveres mandag, ferdig senest onsdag" },
  senuke: { navn: "Sent i uka", forklaring: "Leveres onsdag, ferdig senest fredag" },
  uke: { navn: "Ukeservice", forklaring: "Leveres mandag, ferdig senest fredag" },
};

const startPaDagen = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const dagerTil = (d) => Math.round((startPaDagen(d) - startPaDagen(new Date())) / 86400000);
const ukedagDato = (d) => d.toLocaleDateString("nb-NO", { weekday: "long", day: "numeric", month: "numeric" });

// «i dag», «i morgen» eller «torsdag 29.10.»
function narTekst(d) {
  const n = dagerTil(d);
  if (n === 0) return "i dag";
  if (n === 1) return "i morgen";
  return ukedagDato(d);
}

// Når kunden kan levere: dagservice har et vindu (dagen før eller tidlig på servicedagen),
// de andre typene én dag (mandag eller onsdag).
function leveringTekst(a) {
  if (!a.levering) return narTekst(a.date);
  const { fra, til } = a.levering;
  if (dagerTil(fra) === dagerTil(til)) return `tidlig ${narTekst(til)}`;
  return `${narTekst(fra)} eller tidlig ${narTekst(til)}`;
}

function ferdigTekst({ fra, til }) {
  if (dagerTil(fra) === dagerTil(til)) return narTekst(til);
  return `mellom ${narTekst(fra)} og ${narTekst(til)}`;
}

// Henter JSON og skiller «ingen data» fra «klarte ikke å hente»: en butikk som ikke svarer
// skal ikke se ut som om den har god plass.
async function hentJson(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

// Stor valgknapp i startsidens stil. aria-pressed viser hvilken som er valgt.
function Valg({ valgt, onVelg, ikon, farge = "info", tittel, hjelp }) {
  const f = FARGER[farge] || FARGER.info;
  return (
    <button
      type="button"
      onClick={onVelg}
      aria-pressed={valgt}
      className={`relative flex h-full w-full items-center gap-2 rounded-2xl border-2 p-2.5 sm:p-3 min-h-[4.5rem] text-left transition active:scale-[0.98] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${
        valgt ? "border-indigo-700 bg-indigo-50 ring-2 ring-indigo-700" : f.flate
      }`}
    >
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${f.ikon}`}>
        <Ikon navn={ikon} className="w-7 h-7" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-base sm:text-base font-bold leading-tight text-slate-900 [overflow-wrap:anywhere]">{tittel}</span>
        {hjelp && <span className="mt-0.5 block text-sm leading-snug text-slate-700">{hjelp}</span>}
      </span>
      {valgt && (
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-700 text-white">
          <Ikon navn="hake" className="w-4 h-4" />
        </span>
      )}
    </button>
  );
}

function Steg({ id, nummer, tittel, children }) {
  return (
    <section aria-labelledby={id} className="space-y-2">
      <h2 id={id} className="flex items-center gap-2 text-base font-bold text-slate-900">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-800 text-sm text-white" aria-hidden="true">
          {nummer}
        </span>
        {tittel}
      </h2>
      {children}
    </section>
  );
}

function Melding({ children }) {
  return <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">{children}</p>;
}

// Ett kort per butikk. Øverst det alternativet som gjør sykkelen ferdig tidligst, under de andre.
function SykkelKort({ rad, raskest }) {
  const [forst, ...andre] = rad.alternativer;
  return (
    <li className={`rounded-2xl border-2 bg-white p-3 ${raskest ? "border-emerald-500" : "border-slate-200"}`}>
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
        <h3 className="text-base font-bold text-slate-900">{rad.navn}</h3>
        {raskest && (
          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-sm font-semibold text-emerald-900">Raskest ferdig</span>
        )}
      </div>

      {rad.feil ? (
        <p className="text-red-700">Klarte ikke å hente bookingene for denne butikken. Prøv igjen om litt.</p>
      ) : !forst ? (
        <p className="text-slate-700">Ingen ledig tid de neste ukene.</p>
      ) : (
        <>
          <div className="rounded-xl bg-emerald-50 p-2.5 sm:p-3">
            <p className="text-sm font-semibold text-emerald-900">{TYPER[forst.type].navn}</p>
            <dl className="mt-1 grid gap-x-4 gap-y-2 sm:grid-cols-2">
              <div className="min-w-0">
                <dt className="text-sm text-slate-700">Kan leveres</dt>
                <dd className="text-lg font-bold text-slate-900 first-letter:uppercase">{leveringTekst(forst)}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-slate-700">Ferdig</dt>
                <dd className="text-lg font-bold text-slate-900 first-letter:uppercase">{ferdigTekst(forst.ferdig)}</dd>
              </div>
            </dl>
            <p className="mt-2 text-sm text-slate-700">{TYPER[forst.type].forklaring}.</p>
          </div>

          {andre.length > 0 && (
            <>
              <h4 className="mt-4 mb-2 text-sm font-semibold uppercase tracking-wide text-slate-600">Andre muligheter</h4>
              <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200">
                {andre.map((a) => (
                  <li key={a.type} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-1.5">
                    <span className="w-full font-semibold text-slate-900 sm:w-36">{TYPER[a.type].navn}</span>
                    <span className="text-slate-800">
                      Lever <strong className="font-semibold">{leveringTekst(a)}</strong>, ferdig {ferdigTekst(a.ferdig)}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </li>
  );
}

function SkiKort({ rad, raskest }) {
  return (
    <li className={`rounded-2xl border-2 bg-white p-3 ${raskest ? "border-emerald-500" : "border-slate-200"}`}>
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
        <h3 className="text-base font-bold text-slate-900">{rad.navn}</h3>
        {raskest && (
          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-sm font-semibold text-emerald-900">Raskest ferdig</span>
        )}
      </div>
      {rad.feil ? (
        <p className="text-red-700">Klarte ikke å hente køen for denne butikken. Prøv igjen om litt.</p>
      ) : !rad.dato ? (
        <p className="text-slate-700">Fant ingen ledig ferdigdato de neste ukene.</p>
      ) : (
        <div className="rounded-xl bg-emerald-50 p-2.5 sm:p-3">
          <dl>
            <dt className="text-sm text-slate-700">Ferdig hvis levert i dag</dt>
            <dd className="text-lg font-bold text-slate-900 first-letter:uppercase">
              {rad.batch ? ferdigTekst({ fra: rad.batch.fra, til: rad.batch.til }) : narTekst(rad.dato)}
            </dd>
          </dl>
          {rad.batch && (
            <p className="mt-2 text-sm text-slate-700">
              Slipes samlet én gang i uka. Neste runde: lever senest {narTekst(rad.batch.frist)}.
            </p>
          )}
        </div>
      )}
    </li>
  );
}

// Raskest = tidligst ferdig. Ved likt løp merkes ingen, så merket betyr noe.
function raskesteIndeks(tider) {
  const gyldige = tider.map((t, i) => [t, i]).filter(([t]) => t != null);
  if (gyldige.length < 2) return -1;
  const min = Math.min(...gyldige.map(([t]) => t));
  const like = gyldige.filter(([t]) => t === min);
  return like.length === 1 ? like[0][1] : -1;
}

export default function CapacityCheck() {
  const [kategori, setKategori] = useState("sykkel"); // "sykkel" | "ski" | "skoyter"
  const [filter, setFilter] = useState("generell");
  const [gren, setGren] = useState(""); // "Alpint" | "Langrenn", bare for ski
  const [valgtSkiId, setValgtSkiId] = useState("");

  const [butikker, setButikker] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [minutter, setMinutter] = useState(DEFAULT_MINUTTER);
  // Bookingtyper som er tatt i bruk i tjenestekatalogen, per oppgave (null = ukjent, alle regnes ut).
  const [tillatteTyper, setTillatteTyper] = useState({});
  const [usedByStore, setUsedByStore] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [loadingUsed, setLoadingUsed] = useState(true);
  const [error, setError] = useState("");

  const [stores, setStores] = useState([]);
  const [skiTjenester, setSkiTjenester] = useState([]);
  const [skiTjenesterLastet, setSkiTjenesterLastet] = useState(false);
  const [skiError, setSkiError] = useState("");
  const [skiUsedByStore, setSkiUsedByStore] = useState({});
  const [loadingSkiUsed, setLoadingSkiUsed] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingConfig(true);
      setError("");
      try {
        const [butikkerAll, overridesAll, garanti, dekk1, dekk2, gir, bremse, alleTyper] = await Promise.all([
          getButikkKapasiteter(),
          getKapasitetsOverstyringer(),
          getServiceBySlug("garantihenvendelse").catch(() => null),
          getServiceBySlug("dekk-slangeskift").catch(() => null),
          getServiceBySlug("dekk-lapping").catch(() => null),
          getServiceBySlug("girjustering").catch(() => null),
          getServiceBySlug("bremsejustering").catch(() => null),
          getSykkelBookingtyper().catch(() => null),
        ]);
        if (cancelled) return;
        const dekkService = dekk1 || dekk2;
        const girBremseTider = [gir?.tidsbruk, bremse?.tidsbruk].filter((n) => typeof n === "number");

        setButikker((butikkerAll || []).filter((b) => b?.aktiv !== false));
        // Typene tjenestene kan bookes med: generell = alle sykkeltjenester samlet, ellers tjenesten(e)
        // oppgaven gjelder. Mangler data, vises alle typer som før.
        const sett = (...lister) => {
          const typer = lister.filter(Array.isArray).flat();
          return typer.length ? new Set(typer.map(normBookingtype)) : null;
        };
        const alle = sett(alleTyper);
        setTillatteTyper({
          generell: alle,
          girbremse: sett(gir?.booking, bremse?.booking) || alle,
          reklamasjon: sett(garanti?.booking) || alle,
          dekk: sett(dekkService?.booking) || alle,
        });
        setOverrides(overridesAll || []);
        setMinutter({
          generell: 60,
          reklamasjon: typeof garanti?.tidsbruk === "number" ? garanti.tidsbruk : 10,
          dekk: typeof dekkService?.tidsbruk === "number" ? dekkService.tidsbruk : 10,
          girbremse: girBremseTider.length ? Math.max(...girBremseTider) : 20,
        });
      } catch (e) {
        console.error("Kunne ikke hente kapasitetskonfig:", e);
        if (!cancelled) setError("Klarte ikke å hente kapasiteten til butikkene. Last siden på nytt om litt.");
      } finally {
        if (!cancelled) setLoadingConfig(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!butikker.length) {
      setLoadingUsed(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingUsed(true);
      const today = new Date();
      const from = isoDate(today);
      const to = isoDate(new Date(today.getTime() + DAGER_FRAMOVER * 86400000));
      const entries = await Promise.all(
        butikker.map(async (b) => {
          const verkstedSlug = normalizeWorkshopSlug(b.lokasjonsNavn || b.slug);
          const url = `${API_BASE_URL}/capacity?verkstedSlug=${encodeURIComponent(verkstedSlug)}&from=${from}&to=${to}`;
          try {
            return [verkstedSlug, await hentJson(url)];
          } catch {
            return [verkstedSlug, null];
          }
        })
      );
      if (!cancelled) {
        setUsedByStore(Object.fromEntries(entries));
        setLoadingUsed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [butikker]);

  // Stores (for butikkid <-> verkstedSlug) og ski/skøyte-tjenestene fra tjenestekatalogen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [storesRes, tjenester] = await Promise.all([
          hentJson(`${API_BASE_URL}/stores`),
          getSkiSkoyteTjenester(),
        ]);
        if (cancelled) return;
        setStores(storesRes || []);
        setSkiTjenester((tjenester || []).filter((t) => t.aktiv));
        setSkiTjenesterLastet(true);
      } catch (e) {
        console.error("Kunne ikke hente stores/ski-tjenester:", e);
        if (!cancelled) setSkiError("Klarte ikke å hente ski- og skøytetjenestene. Last siden på nytt om litt.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Brukt ski-/skøytekapasitet pr. butikk (samme backend som foreslå-knappen
  // i ServiceDetailsSki/Skate bruker).
  useEffect(() => {
    const skiButikker = butikker.filter((b) => typeof b.skiDagsbudsjett === "number" && b.skiDagsbudsjett > 0);
    if (!skiButikker.length || !stores.length) {
      setLoadingSkiUsed(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingSkiUsed(true);
      const today = new Date();
      const from = isoDate(today);
      const to = isoDate(new Date(today.getTime() + DAGER_FRAMOVER * 86400000));
      const entries = await Promise.all(
        skiButikker.map(async (cap) => {
          const verkstedSlug = normalizeWorkshopSlug(cap.lokasjonsNavn || cap.slug);
          const store = stores.find((s) => normalizeWorkshopSlug(s.butikknavn) === verkstedSlug);
          if (!store) return [verkstedSlug, {}];
          try {
            return [verkstedSlug, await hentJson(`${API_BASE_URL}/ski-capacity?butikkid=${store.butikkid}&from=${from}&to=${to}`)];
          } catch {
            return [verkstedSlug, null];
          }
        })
      );
      if (!cancelled) {
        setSkiUsedByStore(Object.fromEntries(entries));
        setLoadingSkiUsed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [butikker, stores]);

  const sykkelRader = useMemo(() => {
    return butikker.map((cap) => {
      const verkstedSlug = normalizeWorkshopSlug(cap.lokasjonsNavn || cap.slug);
      const used = usedByStore[verkstedSlug];
      const alternativer = used
        ? computeStoreResults(cap, verkstedSlug, overrides, used, filter, minutter, tillatteTyper[filter] || null)
            .map((r) => ({ ...r, ferdig: ferdigVindu(r) }))
            .sort((a, b) => a.ferdig.til - b.ferdig.til || a.date - b.date)
        : [];
      return { slug: cap.slug, navn: prettifyStoreName(verkstedSlug), feil: used === null, alternativer };
    });
  }, [butikker, usedByStore, overrides, filter, minutter, tillatteTyper]);

  const valgtSki = skiTjenester.find((p) => p._id === valgtSkiId);

  const skiRader = useMemo(() => {
    if (!valgtSki) return [];
    const skiButikker = butikker.filter((b) => typeof b.skiDagsbudsjett === "number" && b.skiDagsbudsjett > 0);
    return skiButikker
      .filter((cap) => {
        const store = stores.find((s) => normalizeWorkshopSlug(s.butikknavn) === normalizeWorkshopSlug(cap.lokasjonsNavn || cap.slug));
        return !valgtSki.tillattButikkIds?.length || (store && valgtSki.tillattButikkIds.includes(store.butikkid));
      })
      .map((cap) => {
        const verkstedSlug = normalizeWorkshopSlug(cap.lokasjonsNavn || cap.slug);
        const used = skiUsedByStore[verkstedSlug];
        if (used === null) return { slug: cap.slug, navn: prettifyStoreName(verkstedSlug), feil: true };
        const forslag = computeFerdigDatoForskslag({
          cap,
          verkstedSlug,
          overridesAll: overrides,
          perDagMin: used?.perDagMin || {},
          dropoffDato: new Date(),
          totalMin: valgtSki.tidsbruk || 0,
          maxLeveringstidDager: valgtSki.leveringstidDager || 0,
          samlesUkentlig: !!valgtSki.samlesUkentlig,
        });
        return { slug: cap.slug, navn: prettifyStoreName(verkstedSlug), dato: forslag?.dato || null, batch: forslag?.visning || null };
      });
  }, [valgtSki, butikker, stores, skiUsedByStore, overrides]);

  // Tjenestene i valgt kategori og gren. Tjenester for begge grener (f.eks. «Annet») står
  // sist i begge, og ski uten gren står i begge, så ingenting forsvinner.
  const tjenesteListe =
    kategori === "skoyter"
      ? skiTjenester.filter((t) => t.serviceType === "Skøyteslip")
      : kategori === "ski" && gren
      ? [
          ...skiTjenester.filter((t) => t.serviceType === "Skiservice" && t.gren.length === 1 && t.gren[0] === gren),
          ...skiTjenester.filter((t) => t.serviceType === "Skiservice" && (t.gren.length !== 1)),
        ]
      : [];

  const velgKategori = (id) => {
    if (id === kategori) return;
    setKategori(id);
    setGren("");
    setValgtSkiId("");
  };
  const velgGren = (id) => {
    if (id === gren) return;
    setGren(id);
    setValgtSkiId("");
  };

  const sykkelRaskest = raskesteIndeks(sykkelRader.map((r) => (r.alternativer[0] ? r.alternativer[0].ferdig.til.getTime() : null)));
  const skiRaskest = raskesteIndeks(
    skiRader.map((r) => (r.feil || !r.dato ? null : startPaDagen(r.batch ? r.batch.til : r.dato).getTime()))
  );
  const trinnTjeneste = kategori === "ski" ? 3 : 2;
  const valgtFilter = FILTERS.find((f) => f.key === filter);

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <Link to={FORSIDE} className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 font-medium text-indigo-700 hover:bg-indigo-50">
        <Ikon navn="tilbake" className="w-5 h-5" /> Tilbake til kontrollpanelet
      </Link>

      <header className="flex items-center gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${FARGER.info.ikon}`}>
          <Ikon navn="kalenderKlokke" className="w-6 h-6" />
        </span>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 text-balance">Sjekk servicekapasitet</h1>
          <p className="text-slate-600">Når kan servicen være ferdig?</p>
        </div>
      </header>

      <Steg id="lt-hva" nummer={1} tittel="Hva gjelder det?">
        <ul className="grid gap-2 sm:grid-cols-3">
          {KATEGORIER.map((k) => (
            <li key={k.id}>
              <Valg valgt={kategori === k.id} onVelg={() => velgKategori(k.id)} ikon={k.ikon} farge={k.farge} tittel={k.tittel} />
            </li>
          ))}
        </ul>
      </Steg>

      {kategori === "sykkel" ? (
        <>
          <Steg id="lt-oppgave" nummer={2} tittel="Hva skal gjøres?">
            <ul className="grid gap-2 sm:grid-cols-2">
              {FILTERS.map((f) => (
                <li key={f.key}>
                  <Valg
                    valgt={filter === f.key}
                    onVelg={() => setFilter(f.key)}
                    ikon={OPPGAVE_IKON[f.key]}
                    farge="verksted"
                    tittel={f.label}
                    hjelp={loadingConfig ? null : `Ca. ${fmtMinutes(minutter[f.key])} arbeid`}
                  />
                </li>
              ))}
            </ul>
          </Steg>

          <Steg id="lt-svar" nummer={3} tittel="Første ledige tid">
            <div aria-live="polite" className="space-y-2">
              {error ? (
                <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{error}</p>
              ) : loadingConfig || loadingUsed ? (
                <Melding>Regner ut ledig tid …</Melding>
              ) : sykkelRader.length === 0 ? (
                <Melding>Fant ingen aktive butikker med kapasitet satt opp.</Melding>
              ) : (
                <>
                  <p className="text-slate-700">
                    For <strong className="font-semibold">{valgtFilter?.label.toLowerCase()}</strong>, regnet ut nå fra
                    kapasiteten og det som allerede er booket.
                    {" "}Service må bookes før kl. 24 dagen før den starter, så service som starter i dag regnes ikke med.
                  </p>
                  <ul className="space-y-2">
                    {sykkelRader.map((rad, i) => (
                      <SykkelKort key={rad.slug} rad={rad} raskest={i === sykkelRaskest} />
                    ))}
                  </ul>
                </>
              )}
            </div>
          </Steg>
        </>
      ) : (
        <>
          {kategori === "ski" && (
            <Steg id="lt-gren" nummer={2} tittel="Alpint eller langrenn?">
              <ul className="grid gap-2 sm:grid-cols-2">
                {GRENER.map((g) => (
                  <li key={g.id}>
                    <Valg valgt={gren === g.id} onVelg={() => velgGren(g.id)} ikon={g.ikon} farge="lager" tittel={g.tittel} />
                  </li>
                ))}
              </ul>
            </Steg>
          )}

          {(kategori === "skoyter" || gren) && (
            <Steg id="lt-tjeneste" nummer={trinnTjeneste} tittel="Hvilken tjeneste?">
              {skiError ? (
                <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{skiError}</p>
              ) : !skiTjenesterLastet ? (
                <Melding>Henter tjenestene …</Melding>
              ) : tjenesteListe.length === 0 ? (
                <Melding>Fant ingen tjenester her. De settes opp under Tjenester og priser.</Melding>
              ) : (
                <ul className="grid gap-2 sm:grid-cols-2">
                  {tjenesteListe.map((p) => (
                    <li key={p._id}>
                      <Valg
                        valgt={valgtSkiId === p._id}
                        onVelg={() => setValgtSkiId(p._id)}
                        ikon={kategori === "skoyter" ? "skoyte" : gren === "Alpint" ? "fjell" : "skispor"}
                        farge="lager"
                        tittel={p.title}
                        hjelp={p.samlesUkentlig ? "Slipes samlet én gang i uka" : p.tidsbruk ? `Ca. ${fmtMinutes(p.tidsbruk)} arbeid` : null}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </Steg>
          )}

          {(kategori === "skoyter" || gren) && (
            <Steg id="lt-ski-svar" nummer={trinnTjeneste + 1} tittel="Foreslått ferdigdato">
              <div aria-live="polite" className="space-y-2">
                {!valgtSki ? (
                  <Melding>Velg en tjeneste over, så ser du når den kan være ferdig i hver butikk.</Melding>
                ) : loadingSkiUsed || loadingConfig ? (
                  <Melding>Regner ut ferdigdato …</Melding>
                ) : skiRader.length === 0 ? (
                  <Melding>Ingen butikker tar imot denne tjenesten.</Melding>
                ) : (
                  <>
                    <p className="text-slate-700">
                      For <strong className="font-semibold">{valgtSki.title}</strong>, ut fra leveringstiden og det som
                      allerede ligger i køen.
                    </p>
                    <ul className="space-y-2">
                      {skiRader.map((rad, i) => (
                        <SkiKort key={rad.slug} rad={rad} raskest={i === skiRaskest} />
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </Steg>
          )}
        </>
      )}
    </div>
  );
}
